<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const placeholderTable = [
  { placeholder: '{nama}', desc: 'Nama pelanggan. Dipakai di hampir semua template supaya pesan terasa personal.', contoh: 'Halo {nama}, ...' },
  { placeholder: '{produk_favorit}', desc: 'Produk yang paling sering dibeli pelanggan tersebut (dihitung otomatis). Kalau pelanggan belum pernah beli apa pun, otomatis diganti teks umum "produk favorit kami" — bukan teks placeholder mentah.', contoh: 'Rindu {produk_favorit} favoritmu?' },
  { placeholder: '{toko}', desc: 'Nama toko Anda.', contoh: 'Salam dari {toko}!' },
]

const faq = [
  {
    q: 'Kenapa filter "Ulang tahun bulan ini" tidak menemukan pelanggan sama sekali, padahal saya yakin ada yang ulang tahun bulan ini?',
    a: 'Filter ini hanya bisa menemukan pelanggan yang tanggal lahirnya SUDAH tercatat di sistem. Saat ini belum ada halaman di aplikasi untuk mengisi/mengubah tanggal lahir pelanggan, jadi kemungkinan besar datanya memang masih kosong untuk semua pelanggan Anda. Fitur input tanggal lahir akan menyusul di update berikutnya.',
  },
  {
    q: 'Klik "Kirim ke [Nama]" tapi tidak ada yang terjadi / WhatsApp tidak terbuka, kenapa?',
    a: 'Cek subjudul di baris pelanggan tersebut — kalau tertulis "(nomor tidak valid)", tombol kirimnya memang sengaja dinonaktifkan karena nomor HP pelanggan itu tidak bisa dikonversi ke format WhatsApp yang benar. Perbaiki dulu nomor HP pelanggan itu di data pelanggannya.',
  },
  {
    q: 'Saya sudah klik "Kirim ke [Nama]" dan statusnya jadi "Dibuka" — apakah pesannya pasti sudah sampai ke pelanggan?',
    a: 'Belum tentu. Status "Dibuka" cuma menandakan Anda sudah menekan tombolnya dan WhatsApp sudah terbuka dengan pesan siap kirim — sistem PintuKasir tidak bisa memastikan Anda benar-benar menekan tombol kirim di WhatsApp setelahnya, karena itu terjadi di aplikasi WhatsApp Anda sendiri, di luar jangkauan sistem.',
  },
  {
    q: 'Bisa kirim ke SEMUA pelanggan sekaligus dengan satu klik?',
    a: 'Sengaja tidak bisa. Ini bukan keterbatasan teknis, tapi keputusan desain — WhatsApp tidak menyediakan cara resmi untuk mengirim pesan otomatis dari aplikasi pihak ketiga tanpa risiko nomor Anda diblokir WhatsApp. Karena itu, tiap pengiriman tetap lewat klik manual satu per satu, memakai WhatsApp Anda sendiri secara wajar seperti biasa.',
  },
  {
    q: 'Kalau saya bikin template dengan channel "Email" atau "WhatsApp & Email", apa gunanya kalau kirim email belum bisa?',
    a: 'Template-nya tetap tersimpan dan siap dipakai kapan pun fitur kirim Broadcast Email sudah tersedia di halaman aplikasi (sedang disiapkan). Untuk sekarang, gunakan template dengan channel "WhatsApp" supaya bisa langsung dipakai di halaman Kirim Broadcast.',
  },
  {
    q: 'Kenapa saya tidak bisa membuat template baru atau mengirim campaign?',
    a: 'Cek izin akun Anda di menu User & Role. Mengelola template (CRUD) dan mengirim broadcast adalah izin terpisah — akun Anda mungkin punya izin lihat/buat template tapi tidak punya izin "Broadcast.send" untuk benar-benar mengirim, atau sebaliknya.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <span class="text-body-2">CRM &amp; Broadcast Marketing</span>
        </div>
        <RouterLink to="/userguide">
          <VBtn
            variant="text"
            prepend-icon="ri-arrow-left-line"
            size="small"
            class="mb-4"
          >
            Kembali ke Daftar Modul
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar
            color="primary"
            variant="tonal"
            size="64"
            rounded="lg"
            class="me-4"
          >
            <VIcon
              icon="ri-megaphone-line"
              size="36"
            />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              CRM &amp; Broadcast Marketing
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Manfaatkan data pelanggan yang sudah otomatis tercatat untuk kirim promosi yang tepat sasaran
            </p>
          </div>
        </div>
        <VAlert
          type="info"
          variant="tonal"
        >
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda dari memahami data pelanggan yang tercatat otomatis, menyaring pelanggan
          sesuai kriteria (segmentasi), membuat template pesan, sampai mengirim broadcast
          WhatsApp ke pelanggan yang tepat.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Alur singkat -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VAlert
          type="success"
          variant="tonal"
        >
          <strong>Alur singkatnya:</strong>
          Data pelanggan tercatat otomatis dari transaksi sehari-hari → Saring pelanggan sesuai
          kriteria saat bikin campaign → Pilih/buat template pesan → Buka antrian, klik kirim
          satu per satu ke tiap pelanggan lewat WhatsApp.
        </VAlert>
      </VCol>
    </VRow>

    <!-- 1. Data Pelanggan Otomatis -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="primary"
            size="small"
            class="me-2"
          >
            1
          </VChip>
          Data Pelanggan Tercatat Otomatis
        </h5>
        <VCard>
          <VCardText>
            <p class="text-body-2 mb-3">
              Begitu seorang pelanggan <strong>dikaitkan ke transaksi</strong> — baik dipilih/
              didaftarkan kasir saat checkout di POS, maupun mengisi nama &amp; nomor HP sendiri
              lewat Self-Order — sistem <strong>otomatis</strong> mencatat &amp; memperbarui data
              berikut, <strong>tanpa Anda perlu input manual satu per satu</strong>:
            </p>
            <VRow>
              <VCol
                cols="12"
                sm="4"
              >
                <VCard
                  variant="tonal"
                  color="info"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <VIcon
                      icon="ri-calendar-line"
                      size="24"
                      class="mb-2"
                    />
                    <p class="text-body-2 font-weight-medium mb-1">
                      Terakhir Belanja
                    </p>
                    <p class="text-caption mb-0">
                      Tanggal transaksi terbarunya — dipakai untuk menyaring pelanggan yang
                      "sudah lama tidak belanja".
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol
                cols="12"
                sm="4"
              >
                <VCard
                  variant="tonal"
                  color="success"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <VIcon
                      icon="ri-star-line"
                      size="24"
                      class="mb-2"
                    />
                    <p class="text-body-2 font-weight-medium mb-1">
                      Produk Favorit
                    </p>
                    <p class="text-caption mb-0">
                      Produk yang paling sering dibelinya — dihitung ulang setiap saat dari
                      riwayat transaksi (selalu akurat, bukan angka lama yang basi).
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol
                cols="12"
                sm="4"
              >
                <VCard
                  variant="tonal"
                  color="warning"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <VIcon
                      icon="ri-money-dollar-circle-line"
                      size="24"
                      class="mb-2"
                    />
                    <p class="text-body-2 font-weight-medium mb-1">
                      Total &amp; Jumlah Belanja
                    </p>
                    <p class="text-caption mb-0">
                      Akumulasi total Rupiah dan jumlah transaksinya — dipakai untuk menandai
                      pelanggan VIP.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
            <p class="text-body-2 mt-4 mb-0">
              Data ini dipakai otomatis oleh 2 fitur di halaman ini: <strong>Segmentasi</strong>
              (Langkah 2, untuk menyaring siapa yang layak dikirimi promosi) dan
              <strong>placeholder pesan Broadcast</strong> (Langkah 3, supaya pesan terasa
              personal per pelanggan).
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 2. Segmentasi -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="primary"
            size="small"
            class="me-2"
          >
            2
          </VChip>
          Segmentasi Pelanggan
        </h5>
        <VCard>
          <VCardText>
            <p class="text-body-2 mb-3">
              Segmentasi (menyaring pelanggan) dilakukan <strong>langsung saat Anda membuat
                campaign broadcast</strong> — bukan halaman terpisah. Buka
              <strong>Marketing → Kirim Broadcast → Buat Campaign</strong>, lalu centang/isi
              kriteria berikut (boleh pilih lebih dari satu):
            </p>
            <VTable class="mb-4">
              <thead>
                <tr>
                  <th>Kriteria</th>
                  <th>Cara Kerja</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Tidak belanja X hari terakhir</strong></td>
                  <td class="text-body-2 py-2">
                    Isi jumlah hari sesuai keinginan. Ikut termasuk pelanggan yang
                    <strong>belum pernah belanja sama sekali</strong> — mereka juga dianggap
                    target relevan untuk diajak transaksi pertama/repeat order.
                  </td>
                </tr>
                <tr>
                  <td><strong>Total belanja minimal (VIP)</strong></td>
                  <td class="text-body-2 py-2">
                    Isi nominal Rupiah — hanya pelanggan dengan akumulasi total belanja
                    sebesar itu atau lebih yang match.
                  </td>
                </tr>
                <tr>
                  <td><strong>Ulang tahun bulan ini</strong></td>
                  <td class="text-body-2 py-2">
                    Centang untuk menyaring pelanggan yang tanggal lahirnya jatuh di bulan
                    berjalan. <strong>Catatan:</strong> filter ini hanya efektif untuk
                    pelanggan yang data tanggal lahirnya sudah terisi (lihat catatan di
                    Langkah 1).
                  </td>
                </tr>
              </tbody>
            </VTable>

            <VAlert
              type="warning"
              variant="tonal"
              class="mb-4"
            >
              <strong>Kombinasi kriteria = DAN, bukan ATAU.</strong> Kalau Anda mencentang
              "VIP" <em>dan</em> "Tidak belanja 30 hari" sekaligus, hasilnya adalah pelanggan
              yang memenuhi <strong>KEDUANYA</strong> secara bersamaan (VIP yang sudah lama
              tidak belanja) — <strong>bukan</strong> gabungan semua pelanggan VIP ditambah
              semua pelanggan yang tidak belanja 30 hari.
            </VAlert>

            <p class="text-body-2 mb-0">
              Setelah kriteria diisi, klik <strong>"Preview Audiens"</strong> — sistem
              menampilkan jumlah dan daftar nama pelanggan yang cocok
              <strong>sebelum</strong> campaign benar-benar dibuat, supaya Anda tahu dulu
              "kena berapa orang" sebelum lanjut.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 3. Template -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="primary"
            size="small"
            class="me-2"
          >
            3
          </VChip>
          Membuat Template Broadcast
        </h5>
        <VCard>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-2">
                Buka <strong>Marketing → Template Broadcast → Tambah Template</strong>.
              </li>
              <li class="mb-2">
                Isi <strong>Nama Template</strong>, pilih <strong>Channel</strong> (WhatsApp,
                Email, atau WhatsApp &amp; Email) dan <strong>Tipe</strong> (Promo, Ulang
                Tahun, Reminder, atau Custom) sesuai kebutuhan.
              </li>
              <li class="mb-2">
                Tulis <strong>Isi Pesan</strong>, pakai placeholder di bawah kalau ingin pesan
                terasa personal per pelanggan.
              </li>
              <li class="mb-0">
                Cek panel <strong>Preview</strong> di sebelah kanan — ganti toggle
                <strong>"Data Lengkap" / "Produk Favorit Kosong"</strong> untuk memastikan
                pesan Anda tetap enak dibaca walau pelanggannya belum punya produk favorit.
              </li>
            </ol>

            <p class="text-body-1 font-weight-medium mb-2">
              Placeholder yang Didukung
            </p>
            <VTable class="mb-0">
              <thead>
                <tr>
                  <th>Placeholder</th>
                  <th>Penjelasan</th>
                  <th>Contoh Pemakaian</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in placeholderTable"
                  :key="row.placeholder"
                >
                  <td><code>{{ row.placeholder }}</code></td>
                  <td class="text-body-2 py-2">
                    {{ row.desc }}
                  </td>
                  <td class="text-body-2 py-2">
                    {{ row.contoh }}
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 4. Kirim Broadcast WhatsApp -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          <VChip
            color="primary"
            size="small"
            class="me-2"
          >
            4
          </VChip>
          Kirim Broadcast WhatsApp
        </h5>
        <VCard>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-2">
                Buka <strong>Marketing → Kirim Broadcast → Buat Campaign</strong>.
              </li>
              <li class="mb-2">
                Pilih <strong>Template Pesan</strong> (khusus template channel WhatsApp), isi
                kriteria segmentasi (Langkah 2), lalu klik <strong>"Preview Audiens"</strong>
                untuk cek dulu siapa saja yang akan masuk.
              </li>
              <li class="mb-2">
                Klik <strong>"Buat Campaign"</strong> — Anda diarahkan ke halaman
                <strong>Antrian Penerima</strong>, berisi daftar tiap pelanggan yang cocok
                kriteria, pesannya sudah dipersonalisasi (placeholder sudah diganti data
                pelanggan masing-masing).
              </li>
              <li class="mb-0">
                Untuk tiap pelanggan, klik tombol <strong>"Kirim ke [Nama]"</strong> —
                WhatsApp/wa.me terbuka di tab baru dengan pesan sudah terisi, tinggal Anda
                tekan kirim di WhatsApp.
              </li>
            </ol>

            <VAlert
              type="error"
              variant="tonal"
              class="mb-4"
            >
              <strong>⚠️ PENTING — Ini BUKAN kirim otomatis sekali klik.</strong> Tidak ada
              tombol "kirim semua sekaligus". Anda tetap harus klik <strong>satu per satu</strong>
              per pelanggan, karena WhatsApp tidak menyediakan cara mengirim pesan otomatis dari
              aplikasi luar tanpa risiko nomor Anda diblokir. Status <strong>"Dibuka"</strong>
              yang muncul setelah Anda klik <strong>hanya menandakan tombolnya sudah ditekan
                dan WhatsApp sudah terbuka</strong> — bukan bukti pesan benar-benar terkirim,
              karena itu tergantung Anda menekan kirim di WhatsApp masing-masing.
            </VAlert>

            <p class="text-body-2 mb-0">
              Progress <strong>"X dari Y sudah dibuka"</strong> di bagian atas halaman membantu
              Anda melacak sudah sampai mana proses klik-nya. Nomor HP pelanggan otomatis
              dikonversi ke format WhatsApp yang benar (mis. <code>08xxx</code> →
              <code>628xxx</code>) — kalau nomornya tidak bisa dikonversi, tombol kirim untuk
              pelanggan itu otomatis nonaktif dan ditandai <em>"nomor tidak valid"</em>.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Catatan Email -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VAlert
          type="warning"
          variant="tonal"
          icon="ri-time-line"
        >
          <strong>📧 Soal Broadcast Email:</strong> Anda bisa membuat template dengan channel
          "Email" atau "WhatsApp &amp; Email" di Langkah 3 — datanya tersimpan dan siap dipakai.
          Namun, halaman untuk benar-benar <strong>mengirim</strong> broadcast lewat Email
          <strong>belum tersedia</strong> di aplikasi saat ini (sedang disiapkan, menyusul di
          update berikutnya). Untuk sekarang, gunakan template dengan channel
          <strong>"WhatsApp"</strong> supaya bisa langsung dipakai di halaman Kirim Broadcast.
        </VAlert>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel
            v-for="(item, i) in faq"
            :key="i"
          >
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Program Loyalty', sub: 'Poin & stempel untuk pelanggan setia', icon: 'ri-vip-diamond-line', color: 'secondary', to: '/userguide/promo-voucher-loyalty/loyalty' },
              { title: 'Promo & Diskon', sub: 'Diskon otomatis tanpa perlu kode', icon: 'ri-price-tag-3-line', color: 'primary', to: '/userguide/promo-voucher-loyalty/promo' },
              { title: 'Voucher & Kupon', sub: 'Diskon lewat kode, bukan otomatis', icon: 'ri-coupon-3-line', color: 'info', to: '/userguide/promo-voucher-loyalty/voucher' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink
              :to="link.to"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar
                    :color="link.color"
                    variant="tonal"
                    size="40"
                    rounded
                  >
                    <VIcon
                      :icon="link.icon"
                      size="22"
                    />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>